import { createAction } from "redux-actions";
import * as authActions from "./AuthActions";
import * as countryActions from "./CountryActions";
import * as dbSubActions from "./DbSubscriptionActions";
import * as navActions from "./NavigationActions";
import { Thunk } from "./Thunk";
import { Api } from "../services/Api";

const prefix = "CONNECTION_";

export class Constants {
    public static STATUS = prefix + "STATUS";
    public static API_STATUS = prefix + "API_STATUS";
    public static SERVICES_STATUS = prefix + "SERVICES_STATUS";
    public static INITIALIZED = prefix + "INITIALIZED";
    public static RESUMED = prefix + "RESUMED";
}

interface StatusPayload {
    all: boolean;
    api: boolean;
    services: boolean;
    displayConnectedWarning: boolean;
    initialized?: boolean;
}

/** Flag that determines if a connection check is the first for the app */
let _isFirstCheck = true;

let _attempts = 0;
let _checkTimer: NodeJS.Timer | undefined;
const _checkTimeout = 35000;

export const initialize = (): Thunk => async (dispatch, getState) => {
    const api = new Api(getState());
    const status = await getStatus(api);
    status.initialized = true;

    if (!status.all)
        _attempts++;

    await dispatch(onStatusUpdate(status));

    // check status again after timeout
    _checkTimer = setTimeout(() => {
        dispatch(beginStatusCheck());
    }, _checkTimeout);
};

export const getStatus = async (api: Api): Promise<StatusPayload> => {
    setTimeout(() => {
        if (_isFirstCheck && typeof window !== "undefined") {
            // it is still the first time checking for ping and we haven't received a response from API yet.
            // API is hangning, so let's try to refresh the browser as it seems to fix the hangning.
            window.location.reload(true);
        }
    }, 35000);

    const apiResult = await api.pingApi();
    const svcResult = await api.pingServices();

    _isFirstCheck = false;

    const status: StatusPayload = {
        all: apiResult.isSuccess, // && svcResult.isSuccess,
        api: apiResult.isSuccess,
        services: svcResult.isSuccess,
        displayConnectedWarning: false,
    };

    return status;
};

export const beginStatusCheck = (): Thunk => async (dispatch, getState) => {
    if (_checkTimer !== undefined)
        clearTimeout(_checkTimer);

    const api = new Api(getState());
    const state = getState();

    const oldStatus: StatusPayload = {
        all: state.conn.connected,
        api: state.conn.apiConnected,
        services: state.conn.servicesConnected,
        displayConnectedWarning: state.conn.displayConnectedWarning,
    };

    const newStatus = await getStatus(api);

    if (newStatus.all) {
        _attempts = 0;
    }
    else {
        _attempts++;
    }

    if (_attempts >= 2) {
        newStatus.displayConnectedWarning = true;
    }
    else {
        newStatus.displayConnectedWarning = false;
    }

    // status changed, so dispatch update
    if (JSON.stringify(oldStatus) !== JSON.stringify(newStatus)) {
        await dispatch(onStatusUpdate(newStatus));

        if (newStatus.all && !oldStatus.all) {
            console.warn("perform initial tasks if not initialized yet");
            dispatch(checkInitialTasks());

            // service resumed
            dispatch(onIsReconnected(true));
        }

        if (!newStatus.all && oldStatus.all) {
            // connection broken
            dispatch(onIsReconnected(false));
        }
    }

    // check status again after timeout
    _checkTimer = setTimeout(() => {
        dispatch(beginStatusCheck());
    }, _checkTimeout + (_attempts > 4 ? _checkTimeout : 0));
};

export const checkInitialTasks = (): Thunk => async (dispatch, getState) => {
    const state = getState();

    await dispatch(authActions.init(state.auth.clientId, state.auth.clientSecret));

    await dispatch(navActions.init());

    await dispatch(dbSubActions.init());

    dispatch(countryActions.init());
};

export const onStatusUpdate = createAction<StatusPayload, StatusPayload>(
    Constants.STATUS, (payload) => (payload),
);

export const onIsReconnected = createAction<boolean, boolean>(
    Constants.RESUMED, (isResumed) => (isResumed),
);



// WEBPACK FOOTER //
// ./src/actions/ConnectionActions.ts